/*
 * Purpose: Slim mission-generated payloads before DB retention (space-conscious)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 100
 */
import type { MissionGeneratedKind } from '@/lib/missionRetentionPolicy';

const MAX_JSON_PREVIEW = 480;
const MAX_INLINE_FIELD = 512;

function summarizeUnknown(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === 'string') {
    return value.length <= MAX_INLINE_FIELD
      ? value
      : { _truncated: true, preview: value.slice(0, MAX_JSON_PREVIEW) };
  }
  try {
    const raw = JSON.stringify(value);
    if (raw.length <= MAX_INLINE_FIELD) return value;
    return { _truncated: true, preview: raw.slice(0, MAX_JSON_PREVIEW) };
  } catch {
    return { _truncated: true };
  }
}

function slimPlan(payload: Record<string, unknown>): Record<string, unknown> {
  const preview = payload.workflowPreview as
    | { name?: string; nodes?: unknown[]; edges?: unknown[] }
    | undefined;
  const { workflowPreview: _wp, dryRunFixture: _df, ...rest } = payload;
  return {
    ...rest,
    workflowPreview: preview
      ? {
          name: preview.name,
          nodeCount: Array.isArray(preview.nodes) ? preview.nodes.length : 0,
          edgeCount: Array.isArray(preview.edges) ? preview.edges.length : 0,
        }
      : undefined,
    dryRunFixture: payload.dryRunFixture ? { _omitted: 'fixture not duplicated in DB' } : undefined,
  };
}

function slimDryRun(payload: Record<string, unknown>): Record<string, unknown> {
  const stepResults = payload.stepResults;
  if (!Array.isArray(stepResults)) return payload;
  return {
    ...payload,
    stepResults: stepResults.map((step) => {
      if (!step || typeof step !== 'object') return step;
      const s = step as Record<string, unknown>;
      return {
        ...s,
        input: summarizeUnknown(s.input),
        output: summarizeUnknown(s.output),
      };
    }),
  };
}

function slimIntegrationProposals(payload: Record<string, unknown>): Record<string, unknown> {
  const proposals = payload.proposals;
  if (!Array.isArray(proposals)) return payload;
  return {
    ...payload,
    proposals: proposals.map((p) => {
      if (!p || typeof p !== 'object') return p;
      const row = p as Record<string, unknown>;
      const connector = row.connector as Record<string, unknown> | undefined;
      if (!connector) return row;
      const { defaultConfig: _dc, ...connectorRest } = connector;
      return {
        ...row,
        connector: {
          ...connectorRest,
          defaultConfig: connector.defaultConfig ? { _omitted: 'config in library on approve' } : undefined,
        },
      };
    }),
  };
}

/** Strip redundant / bulky fields before gzip + Postgres retain. */
export function slimPayloadForRetention(
  kind: MissionGeneratedKind,
  payload: Record<string, unknown>
): Record<string, unknown> {
  switch (kind) {
    case 'plan':
      return slimPlan(payload);
    case 'dry_run':
      return slimDryRun(payload);
    case 'integration_proposals':
      return slimIntegrationProposals(payload);
    default:
      return payload;
  }
}
